import { cloneDeep, sortBy } from "lodash";

import { SettingApi } from "lib/api";
import { ApiHelper, Http } from "lib/api/base";

import {
	ComponentTemplate,
	DataFieldTemplate,
	MultipleItemRequirement,
	Requirement,
	RequirementItem,
	SectionTemplate,
	SingleItemRequirement
} from "lib/data/model";


export class SectionTemplateApi {

	public static getSectionTemplateById = async (id?: number, skipFlatten?: boolean): Promise<SectionTemplate | undefined> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/sectionTemplate/${id}`;

			// Make Request
			const response: Response = await Http.httpGet(url, undefined);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const sectionTemplate: SectionTemplate = await ApiHelper.parseResponseBody(response);

				// make additions to SectionTemplate to make it functional for our page(flatten requirement items)

				if (!skipFlatten) {
					sectionTemplate.templateRequirementList = SectionTemplateApi.addFlatItemsToSectionTemplate(sectionTemplate);
				}
				return sectionTemplate;
			}
			else {
				const errorResponse = await ApiHelper.parseResponseBody(response);
				return errorResponse;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return undefined;
	}

	public static getSectionTemplateHierarchyById = async (id: string): Promise<SectionTemplate[] | []> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/sectionTemplate/${id}/hierarchy`;

			// Make Request
			const response: Response = await Http.httpGet(url, undefined);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const sectionTemplates: SectionTemplate[] = await ApiHelper.parseResponseBody(response);

				// make additions to SectionTemplate to make it functional for our page(flatten requirement items)


				return sectionTemplates;
			}
			else {
				const errorResponse = await ApiHelper.parseResponseBody(response);
				return errorResponse;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return [];
	}

	public static createSectionTemplateBy = async (sectionTemplate: SectionTemplate): Promise<SectionTemplate | undefined> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/sectionTemplate`;

			// Make Request
			const response: Response = await Http.httpPost(url, sectionTemplate);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const createdSectionTemplate: SectionTemplate = await ApiHelper.parseResponseBody(response);

				return createdSectionTemplate;
			}
			else {
				const errorResponse = await ApiHelper.parseResponseBody(response);
				return errorResponse;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return undefined;
	}

	public static deleteSectionTemplateById = async (id?: number): Promise<SectionTemplate | undefined> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/sectionTemplate/${id}`;

			// Make Request
			const response: Response = await Http.httpDelete(url, undefined);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const sectionTemplate: SectionTemplate = await ApiHelper.parseResponseBody(response);

				return sectionTemplate;
			}
			else {
				const errorResponse = await ApiHelper.parseResponseBody(response);
				return errorResponse;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return undefined;
	}

	public static getDirectorySectionTemplateRequirements = async (): Promise<SectionTemplate | null> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/sectionTemplate/`;

			// Make Request
			const response: Response = await Http.httpGet(url, undefined);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const sectionTemplates: SectionTemplate = await ApiHelper.parseResponseBody(response);




				// make additions to SectionTemplate to make it functional for our page(flatten requirement items)
				return sectionTemplates;
			}
			else {
				const errorResponse = await ApiHelper.parseResponseBody(response);
				return errorResponse;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return null;
	}

	public static updateSectionTemplate = async (id: number, sectionTemplateIn?: SectionTemplate): Promise<SectionTemplate | undefined> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl && sectionTemplateIn) {

			// Configure Url
			const url: string = `${baseUrl}/sectionTemplate/${id}`;
			const sectionTemplate = cloneDeep(sectionTemplateIn);

			if (sectionTemplate.dataTemplate) {
				sectionTemplate.dataTemplate.requirements.forEach((requirement) => {
					switch (requirement.$type) {
						case "MultipleItemRequirement<DataFieldTemplate>":
							const multipleItemRequirement = requirement as MultipleItemRequirement<ComponentTemplate>;
							multipleItemRequirement.items.forEach((item) => {
								if (item.owningRequirement) {
									delete item.owningRequirement;
								}
							});
							break;
						case "SingleItemRequirement<DataFieldTemplate>":
							const singleItemRequirement = requirement as SingleItemRequirement<ComponentTemplate>;
							if (singleItemRequirement.item.owningRequirement) {
								delete singleItemRequirement.item.owningRequirement;
							}
							break;

						default:
							break;
					}

				});
			}
			sectionTemplate.requirements.forEach((requirement) => {
				switch (requirement.$type) {
					case MultipleItemRequirement.$type.ComponentTemplate:
						const multipleItemRequirement = requirement as MultipleItemRequirement<ComponentTemplate>;
						multipleItemRequirement.items.forEach((item) => {
							delete item.owningRequirement;
							if (item.item.$type === SectionTemplate.$type) {
								const multipleItemRequirementSectionTemplate = item.item as SectionTemplate;
								delete multipleItemRequirementSectionTemplate.requirements;
							}
						});
						break;
					case SingleItemRequirement.$type.ComponentTemplate:
						const singleItemRequirement = requirement as SingleItemRequirement<ComponentTemplate>;
						delete singleItemRequirement.item.owningRequirement;
						if (singleItemRequirement.item.item.$type === SectionTemplate.$type) {
							const singleItemRequirementSectionTemplate = singleItemRequirement.item.item as SectionTemplate;
							delete singleItemRequirementSectionTemplate.requirements;
						}
						break;

					default:
						break;
				}

			});

			// Make Request
			const response: Response = await Http.httpPut(url, sectionTemplate);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const updatedSectionTemplate: SectionTemplate = await ApiHelper.parseResponseBody(response);

				// make additions to SectionTemplate to make it functional for our page(flatten requirement items)
				updatedSectionTemplate.templateRequirementList = SectionTemplateApi.addFlatItemsToSectionTemplate(updatedSectionTemplate);

				return updatedSectionTemplate;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return undefined;
	}

	private static addOwningItemToDataTemplateRequirement = (sectionTemplate: SectionTemplate) => {
		// Loop through dataTemplate requirements
		if (sectionTemplate.dataTemplate) {
			sectionTemplate.dataTemplate.requirements.forEach((requirement) => {
				switch (requirement.$type) {
					// if it is a multi item req
					case MultipleItemRequirement.$type.DataFieldTemplate:
						const multipleItemRequirement = requirement as MultipleItemRequirement<DataFieldTemplate>;
						// push out the requirements in items to the requirementItems list
						multipleItemRequirement.items.forEach((item) => {
							if (item) {
								item.owningRequirement = multipleItemRequirement;
							}
						});

						break;
					case SingleItemRequirement.$type.DataFieldTemplate:
						const singleItemRequirement = requirement as SingleItemRequirement<DataFieldTemplate>;
						// push single item out to requirements list
						if (singleItemRequirement.item) {
							singleItemRequirement.item.owningRequirement = singleItemRequirement;
						}

						break;
					default:
						break;
				}

			});
		}
		else {
			// Temp fix to make the ui not break if there is no dataTemplate
		}
	}

	private static addFlatItemsToSectionTemplate = (sectionTemplate: SectionTemplate) => {

		SectionTemplateApi.addOwningItemToDataTemplateRequirement(sectionTemplate);
		// create a list to store the flattend list items
		const templateRequirementList: Array<RequirementItem<ComponentTemplate>> = [];
		// loop through section requirements
		sectionTemplate.requirements.forEach((requirement: Requirement) => {
			switch (requirement.$type) {
				// if it is a multi item req
				case MultipleItemRequirement.$type.ComponentTemplate:
					const multipleItemRequirement = requirement as MultipleItemRequirement<ComponentTemplate>;
					// push out the requirements in items to the requirementItems list
					multipleItemRequirement.items.forEach((requirementItem) => {
						if (requirementItem) {
							requirementItem.owningRequirement = multipleItemRequirement;
							templateRequirementList.push(requirementItem);
						}
					});
					break;
				case SingleItemRequirement.$type.ComponentTemplate:
					const singleItemRequirement = requirement as SingleItemRequirement<ComponentTemplate>;
					// push single item out to requirements list
					if (singleItemRequirement.item) {
						singleItemRequirement.item.owningRequirement = singleItemRequirement;
						templateRequirementList.push(singleItemRequirement.item);
					}
					break;
				default:
					break;
			}
		});

		return sortBy(templateRequirementList, ["initialOrder"]);
	}
}
